import { Component, Inject, OnInit, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization } from 'app/models/organization.model';
import { combineLatest, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Currency } from 'app/models/currency.model';
import { TranslateService } from "@ngx-translate/core";
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import {
  ComboGroup,
  ComboGroupStructure,
  DisplayedProduct,
  DisplayedSize,
  DisplayedSizeStructure,
  MenuSection,
  ModifierGroup,
  ModifierGroupStructure,
  Nutrition,
  nutritionFields,
  OverrideInstruction,
  OverridePosition,
  OverridePositionRequest,
  PositionTypeEnum,
  Product,
  ProductOverride,
  Size,
  DisplayedSizeScale,
  SizeStructure,
  SizeStructureOverride,
  SizeScale,
  OverridePositionsMap,
  emptyOverride,
  DisplayedComboGroupStructure,
  RelatedProductsProgram,
} from 'app/models/menu.model';
import { MenuOverrideService } from 'app/services/menu-override.service';
import { BaseModalComponent } from 'app/shared/base-modal/base-modal.component';
import { PosOrganizationModel } from "app/reducers/pos.reducer";
import * as MenuSelectors from 'app/reducers/menu.selectors';
import { Store } from '@ngrx/store';
import { MenuService } from 'app/services/menu.service';
import {
  EditSizeModalComponent,
  EditSizeModalComponentDataType
} from 'app/pages/menu/shared/edit-size-modal/edit-size-modal.component';
import {
  EditSizeScaleModalComponent,
  EditSizeScaleModalComponentDataType
} from "app/pages/menu/shared/edit-size-scale-modal/edit-size-scale-modal.component";
import { SizeScaleService } from "app/services/size-scale.service";
import { setSizeScales } from "app/reducers/menu.actions";
import {
  DELETE_SIZE_SCALE,
  DeleteSizeScaleDialogComponent, DeleteSizeScaleDialogData
} from "app/pages/menu/shared/delete-size-scale-dialog/delete-size-scale-dialog.component";
import {
  SelectProductsModalComponent, SelectProductsModalComponentDataType
} from "app/pages/menu/shared/select-products-modal/select-products-modal.component";
import { defaultSizeDisabled } from 'app/services/default-sizeId-override.service';
import { environment } from 'environments/environment';
import { nutritionControlName } from 'app/pages/menu/shared/nutrition-form-field/nutrition-form-field.component';

@UntilDestroy()
@Component({
  selector: 'app-edit-product-modal',
  templateUrl: './edit-product-modal.component.html',
  styleUrls: ['./edit-product-modal.component.scss']
})
export class EditProductModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {

  form: FormGroup;
  readonly enableRelatedProductsV3: boolean = environment.enableRelatedProductsV3;
  readonly enableNutritionEditing: boolean = environment.enableNutritionEditing;
  readonly showLegacyRelatedProducts: boolean = false;
  readonly showDetailedDescription: boolean = !!environment.kioskBaseUrl;
  saveForAll: boolean = false;
  saveForAllCombo: boolean = false;
  saveForAllProductsInComboGroup: boolean = false;
  isBeingSaveProcessed = false;
  currency = null;
  sizeScales: DisplayedSizeScale[] = [];

  viewInit = false;

  selectedDishImgSrc: string;
  overrideDishImgSrc: string;
  hasPrice = true;
  newTags: string[] = [];
  newTagIds: string[] = [];
  relatedProducts: string[] = [];
  defaultSizeId: string = null;
  rpProgramIds: string[] = [];

  formTouched = false;
  newAllergenGroups: string[] = [];
  disableSubmit: boolean;

  initValues = {
    defaultSizeId: undefined,
  };
  originalDefaultSizesMap: Map<string, boolean>; // key - sizeId, value - isDefault
  selectedMenuSections = {};

  hideSizesImages: boolean;
  offerToCollect: boolean;
  sizes: DisplayedSizeStructure[];
  dictionarySizes: Map<string, DisplayedSize>;
  originalDictionarySizes: Map<string, Size>;
  dictionaryModifierGroups: Map<string, ModifierGroup>;
  originalDictionaryModifierGroups: Map<string, ModifierGroup>;
  dictionaryComboGroups: Map<string, ComboGroup>;
  originalDictionaryComboGroups: Map<string, ComboGroup>;
  dictionaryProducts: Map<string, DisplayedProduct>;
  originalDictionaryProducts: Map<string, Product>;
  overridePositionsMap: OverridePositionsMap;

  changesInformationMessage = '';
  products: Map<string, Product> = new Map<string, Product>();
  products$: Observable<Map<string, Product>>;

  menuOrganization$: Observable<PosOrganizationModel>;
  currency$: Observable<Currency>;
  dictionaryProducts$: Observable<Map<string, DisplayedProduct>>;
  originalDictionaryProducts$: Observable<Map<string, Product>>;
  dictionarySizes$: Observable<Map<string, DisplayedSize>>;
  originalDictionarySizes$: Observable<Map<string, Size>>;
  dictionaryModifierGroups$: Observable<Map<string, ModifierGroup>>;
  originalDictionaryModifierGroups$: Observable<Map<string, ModifierGroup>>;
  dictionaryComboGroups$: Observable<Map<string, ComboGroup>>;
  originalDictionaryComboGroups$: Observable<Map<string, ComboGroup>>;
  sizeScales$: Observable<DisplayedSizeScale[]>;

  overridePosition: OverridePosition;

  modifiersDisplayTypes: string[] = ['Single', 'All'];

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<EditProductModalComponent>,
    private translate: TranslateService,
    private store: Store,
    private menuService: MenuService,
    private sizeScaleService: SizeScaleService,
    @Inject(MAT_DIALOG_DATA) public data: EditProductsModalComponentDataType
  ) {
    super(dialogRef);
  }

  ngAfterViewInit(): void {
    this.viewInit = true;
  }

  get isUpdateMode(): boolean {
    return !!this.data?.product;
  }

  ngOnInit(): void {
    this.menuOrganization$ = this.store.select(MenuSelectors.selectMenuOrganization);
    this.currency$ = this.store.select(MenuSelectors.selectCurrency);
    this.dictionaryProducts$ = this.store.select(MenuSelectors.selectDictionaryProducts);
    this.originalDictionaryProducts$ = this.store.select(MenuSelectors.selectOriginalDictionaryProducts);
    this.dictionarySizes$ = this.store.select(MenuSelectors.selectDictionarySizes);
    this.originalDictionarySizes$ = this.store.select(MenuSelectors.selectOriginalDictionarySizes);
    this.dictionaryModifierGroups$ = this.store.select(MenuSelectors.selectDictionaryModifierGroup);
    this.originalDictionaryModifierGroups$ = this.store.select(MenuSelectors.selectOriginalDictionaryModifierGroup);
    this.dictionaryComboGroups$ = this.store.select(MenuSelectors.selectDictionaryComboGroup);
    this.originalDictionaryComboGroups$ = this.store.select(MenuSelectors.selectOriginalDictionaryComboGroup);
    this.sizeScales$ = this.store.select(MenuSelectors.selectSizeScales);
    this.products$ = this.store.select(MenuSelectors.selectDictionaryProducts);

    this.relatedProducts = this.data.product.relatedProducts;
    this.selectedDishImgSrc = this.data.product.externalPictureUrl
    this.newTags = this.data.product.tags;
    this.newTagIds = this.data.product.tagIds;
    this.rpProgramIds = this.data.product.relatedProductsProgramIds;
    this.newAllergenGroups = this.data.product.productStructure?.allergenGroups;

    this.originalDefaultSizesMap = this.convertSizesToMap(this.data.originalProduct.productStructure?.sizes);
    this.defaultSizeId = this.data.product.productStructure?.defaultSizeId;

    combineLatest([
      this.products$])
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(([products]) => {
        this.products = products;
      });

    this.store.select(MenuSelectors.selectOverridePositionsMap)
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(overridePositionsMap => {
        this.overridePositionsMap = overridePositionsMap;

        const productOverridePosition = this.overridePositionsMap.getOverride(PositionTypeEnum.Product, this.data.product.externalId);
        if (productOverridePosition) {
          const productOverride = JSON.parse(productOverridePosition.override) as ProductOverride;
          if (productOverride.externalPictureUrl) {
            this.overrideDishImgSrc = productOverride.externalPictureUrl;
          }
        }
      });
    this.overridePosition = this.overridePositionsMap.getOverride(PositionTypeEnum.Product, this.data.product.externalId);

    this.currency$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(currency => {
        this.currency = currency;
      });

    this.dictionaryProducts$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(dictionaryProducts => {
        this.dictionaryProducts = dictionaryProducts;
        this.data.product = JSON.parse(JSON.stringify(this.dictionaryProducts.get(this.data.product.externalId)));
      });

    this.originalDictionaryProducts$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(dictionaryProducts => {
        this.originalDictionaryProducts = dictionaryProducts;
      });

    this.dictionarySizes$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(dictionarySizes => {
        this.dictionarySizes = dictionarySizes;
      });

    this.originalDictionarySizes$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(originalDictionarySizes => {
        this.originalDictionarySizes = originalDictionarySizes;
      });

    this.dictionaryModifierGroups$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(dictionaryModifierGroups => {
        this.dictionaryModifierGroups = dictionaryModifierGroups;
      });

    this.originalDictionaryModifierGroups$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(dictionaryModifierGroups => {
        this.originalDictionaryModifierGroups = dictionaryModifierGroups;
      });

    this.originalDictionaryComboGroups$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(dictionaryComboGroups => {
        this.originalDictionaryComboGroups = dictionaryComboGroups;
      });

    this.dictionaryComboGroups$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(dictionaryComboGroups => {
        this.dictionaryComboGroups = dictionaryComboGroups;
      });

    this.sizeScales$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(sizeScales => {
        this.sizeScales = sizeScales;
      });

    const localProduct = this.data?.product ? {
      ...this.data.product
    } : null;

    this.hideSizesImages = localProduct.hideSizesImages;

    this.initValues['dishName'] = localProduct.name ? localProduct.name : '';
    this.initValues['externalPictureUrl'] = localProduct.externalPictureUrl ? localProduct.externalPictureUrl : '';
    this.initValues['weight'] = localProduct.weight ? localProduct.weight : 0;
    this.initValues['measure'] = localProduct.measure ? localProduct.measure : '';
    this.initValues['description'] = localProduct.description ? localProduct.description : '';
    this.initValues['detailedDescription'] = localProduct.detailedDescription ? localProduct.detailedDescription : '';
    this.initValues['tags'] = this.data.product.tags;
    this.initValues['tagIds'] = this.data.product.tagIds;
    this.initValues['rpProgramIds'] = this.data.product.relatedProductsProgramIds ?? [];
    this.initValues['allergenGroups'] = this.data.product.productStructure?.allergenGroups;
    this.initValues['hideSizesImages'] = localProduct.hideSizesImages;
    this.offerToCollect = localProduct.comboStructure?.offerToCollect ?? true;
    this.initValues['offerToCollect'] = this.offerToCollect;
    this.initValues['menuMediaUrl'] = localProduct.mediaInfo?.menuMediaUrl ?? '';
    this.initValues['cardMediaUrl'] = localProduct.mediaInfo?.cardMediaUrl ?? '';
    this.initValues['modifiersDisplayType'] = localProduct.productStructure?.modifiersDisplayType;
    this.initValues['relatedProductsName'] = localProduct.relatedProductsName;
    this.initValues['relatedProductsNameTranslations'] = localProduct.relatedProductsNameTranslations;
    this.initValues['relatedProducts'] = localProduct.relatedProducts;
    this.initValues.defaultSizeId = localProduct.productStructure?.defaultSizeId;

    this.form = new FormGroup({
      dishName: new FormControl(this.initValues['dishName'], [Validators.required]),
      picture: new FormControl(this.initValues['externalPictureUrl']),
      weight: new FormControl(this.initValues['weight'], [Validators.required]),
      measure: new FormControl(this.initValues['measure']),
      description: new FormControl(this.initValues['description']),
      detailedDescription: new FormControl(this.initValues['detailedDescription']),
      menuMediaUrl: new FormControl(this.initValues['menuMediaUrl']),
      cardMediaUrl: new FormControl(this.initValues['cardMediaUrl']),
      modifiersDisplayType: new FormControl(this.initValues['modifiersDisplayType']),
      relatedProductsName: new FormControl(this.initValues['relatedProductsName']),
    });

    this.addNutritionControls('nutrition', localProduct.nutrition);

    let sizeScaleId = null;
    if (localProduct?.productStructure?.sizes.length > 0) {
      for (const size of localProduct?.productStructure.sizes) {
        this.form.addControl(size.sizeId, new FormControl(size.price, [Validators.min(0)]));
        this.form.addControl(size.sizeId + "weight", new FormControl(size.weight, [Validators.min(0)]));
        this.form.addControl('isHidden' + size.sizeId, new FormControl(size.isHidden));
        this.form.addControl('externalPictureUrl' + size.sizeId, new FormControl(size.externalPictureUrl));
        this.initValues[ size.sizeId ] = size.price;
        this.initValues[ size.sizeId + "weight" ] = size.weight;
        this.initValues[ 'isHidden' + size.sizeId ] = size.isHidden;
        this.initValues[ 'externalPictureUrl' + size.sizeId ] = size.externalPictureUrl;
        this.addNutritionControls('nutrition' + size.sizeId, size.nutrition);
      }
      this.form.addControl("isShowHideSizes", new FormControl(false, []));
      this.form.addControl("hideSizesImages", new FormControl(this.hideSizesImages, []));
      this.hasPrice = false;
      this.sizes = this.data.product.productStructure.sizes.filter(s => s.isHidden === false).sort((a, b) => a.order - b.order);

      sizeScaleId = localProduct.productStructure.sizeScaleId;
      this.form.addControl("sizeScaleId", new FormControl(sizeScaleId))
      this.initValues[ 'sizeScaleId' ] = sizeScaleId;
    }

    if (localProduct?.productStructure){
      let productPrice = localProduct.productStructure.price;
      this.form.addControl("dishPrice", new FormControl(productPrice, [Validators.required, Validators.min(0)]));
      this.initValues['dishPrice'] = productPrice;
    } else if (localProduct?.comboStructure) {
      let productPrice = localProduct.comboStructure.price;
      this.form.addControl("dishPrice", new FormControl(productPrice, [Validators.required, Validators.min(0)]));
      this.initValues['dishPrice'] = productPrice;
    }

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        let nameValue = '';
        if (this.data.product.nameTranslations.hasOwnProperty(lang)) {
          nameValue = this.data.product.nameTranslations[lang];
        }
        this.form.addControl('productName' + lang, new FormControl(nameValue, []));
        this.initValues['productName' + lang] = nameValue;

        let descriptionValue = '';
        if (this.data.product.descriptionTranslations.hasOwnProperty(lang)) {
          descriptionValue = this.data.product.descriptionTranslations[lang];
        }
        this.form.addControl('productDescription' + lang, new FormControl(descriptionValue, []));
        this.initValues['productDescription' + lang] = descriptionValue;

        let detailedDescriptionValue = '';
        if (this.data.product.detailedDescriptionTranslations && this.data.product.detailedDescriptionTranslations.hasOwnProperty(lang)) {
          detailedDescriptionValue = this.data.product.detailedDescriptionTranslations[lang];
        }
        this.form.addControl('productDetailedDescription' + lang, new FormControl(detailedDescriptionValue, []));
        this.initValues['productDetailedDescription' + lang] = detailedDescriptionValue;

        let relatedProductsNameValue = '';
        if (this.data.product.relatedProductsNameTranslations && this.data.product.relatedProductsNameTranslations.hasOwnProperty(lang)){
          relatedProductsNameValue = this.data.product.relatedProductsNameTranslations[lang];
        }
        this.form.addControl('relatedProductsName' + lang, new FormControl(relatedProductsNameValue, []));
        this.initValues['relatedProductsName' + lang] = relatedProductsNameValue;
      }
    }

    this.form.valueChanges.subscribe(value => {
      this.formTouched = true;
    })
  }

  private addNutritionControls(controlPrefix: string, nutrition: Nutrition | null | undefined) {
    for (const field of nutritionFields) {
      const controlName = nutritionControlName(controlPrefix, field);
      const value = nutrition?.[field] ?? null;
      this.form.addControl(controlName, new FormControl(value, [Validators.min(0)]));
      this.initValues[controlName] = value;
    }
  }

  private getFormNutrition(controlPrefix: string): Nutrition {
    const nutrition: Nutrition = {};
    for (const field of nutritionFields) {
      nutrition[field] = this.form.value[nutritionControlName(controlPrefix, field)] ?? null;
    }
    return nutrition;
  }

  private isNutritionFormChanged(controlPrefix: string): boolean {
    return nutritionFields.some(field => {
      const controlName = nutritionControlName(controlPrefix, field);
      return (this.initValues[controlName] ?? null) !== (this.form.value[controlName] ?? null);
    });
  }

  onChangeInformationMessage($event: string[]) {
    this.formTouched = true;
    this.changesInformationMessage = this.menuService.getUpdateInformationMessage($event);
  }

  updateChangesInformationMessage() {
    let changes = this.checkFormChanges();

    this.changesInformationMessage = this.menuService.getUpdateInformationMessage(changes);
  }

  private checkFormChanges() {
    let changes = [];
    if(this.initValues['menuMediaUrl']?.trim() != this.form.value['menuMediaUrl']?.trim()){
      changes.push(`"${this.translate.instant('MENU.MENU_MEDIA_URL')}"`);
    }
     if(this.initValues['cardMediaUrl']?.trim() != this.form.value['cardMediaUrl']?.trim()){
      changes.push(`"${this.translate.instant('MENU.CARD_MEDIA_URL')}"`);
    }
    if (this.initValues['dishName'].trim() !== this.form.value['dishName'].trim()) {
      changes.push(`"${this.translate.instant('MENU.NAME')}"`);
    }
    if ((this.initValues['description'] ?? '').trim() !== (this.form.value['description'] ?? '').trim()) {
      changes.push(`"${this.translate.instant('MENU.DESCRIPTION')}"`);
    }
    if ((this.initValues['detailedDescription'] ?? '').trim() !== (this.form.value['detailedDescription'] ?? '').trim()) {
      changes.push(`"${this.translate.instant('MENU.DETAILED_DESCRIPTION')}"`);
    }
    if (this.initValues['externalPictureUrl'] !== this.form.value['externalPictureUrl']) {
      changes.push(`"${this.translate.instant('MENU.PICTURE')}"`);
    }
    if (this.initValues['weight'] !== this.form.value['weight']) {
      changes.push(`"${this.translate.instant('MENU.WEIGHT')}"`);
    }
    if ((this.initValues['measure'] ?? '').trim() !== (this.form.value['measure'] ?? '').trim()) {
      changes.push(`"${this.translate.instant('MENU.MEASURE')}"`);
    }
    if (this.isNutritionFormChanged('nutrition')) {
      changes.push(`"${this.translate.instant('MENU.NUTRITION.TITLE')}"`);
    }
    if (this.initValues['modifiersDisplayType'] !== this.form.value['modifiersDisplayType']) {
      changes.push(`"${this.translate.instant('MENU.MODIFIERS_DISPLAY_TYPE')}"`);
    }
    if ([...this.initValues['tags']].sort().toString() !== [...this.newTags].sort().toString()) {
      changes.push(`"${this.translate.instant('MENU.TAGS')}"`);
    }
    if ([...this.initValues['tagIds']].sort().toString() !== [...this.newTagIds].sort().toString()) {
      changes.push(`"${this.translate.instant('MENU.TAGS')}"`);
    }
    if ([...this.initValues['rpProgramIds']].sort().toString() !== [...this.rpProgramIds].sort().toString()) {
      changes.push(`"${this.translate.instant('MENU.RP_PROGRAMS')}"`);
    }
    if(this.initValues['allergenGroups'] && [...this.initValues['allergenGroups']]?.sort().toString() !== [...this.newAllergenGroups]?.sort().toString()) {
      changes.push(`"${this.translate.instant('MENU.ALLERGEN_GROUPS')}"`);
    }
    if (this.data?.product?.productStructure?.sizes.length > 0) {
      for (const size of this.data?.product?.productStructure.sizes) {
        var sizeName = this.getSizeName(size.sizeId);
        if (this.initValues[size.sizeId] !== this.form.value[size.sizeId]) {
          changes.push(`"${sizeName} ${this.translate.instant('MENU.PRICE')}"`);
        }
        if (this.initValues['isHidden' + size.sizeId] !== this.form.value['isHidden' + size.sizeId]) {
          const changedText = this.form.value['isHidden' + size.sizeId] ?
            this.translate.instant('MENU.IS_HIDDEN') :
            this.translate.instant('MENU.IS_VISIBLE');
          changes.push(`"${sizeName} ${changedText}"`);
        }
        if (this.isNutritionFormChanged('nutrition' + size.sizeId)) {
          changes.push(`"${sizeName} ${this.translate.instant('MENU.NUTRITION.TITLE')}"`);
        }
      }
      if(this.initValues['sizeScaleId'] !== this.form.value['sizeScaleId']){
        changes.push(`"${this.translate.instant('MENU.SIZE_SCALE')}"`)
      }
      if (this.initValues['dishPrice'] !== this.form.value['dishPrice']) {
        changes.push(`"${this.translate.instant('MENU.PRICE')}"`);
      }
    }

    if (this.initValues['relatedProductsName']?.trim() !== this.form.value['relatedProductsName']?.trim()) {
      changes.push(`"${this.translate.instant('MENU.DISHES.NEW.RELATED_PRODUCTS_NAME')}"`);
    }
    if ([...this.initValues['relatedProducts']].sort().toString() !== [...this.relatedProducts].sort().toString()) {
      changes.push(`"${this.translate.instant('MENU.DISHES.NEW.RELATED_PRODUCTS')}"`);
    }

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if (this.initValues['productName' + lang].trim() !== this.form.value['productName' + lang].trim()) {
          changes.push(`"${this.translate.instant('MENU.NAME')} ${lang}"`);
        }
        if ((this.initValues['productDescription' + lang] ?? '').trim() !== (this.form.value['productDescription' + lang] ?? '').trim()) {
          changes.push(`"${this.translate.instant('MENU.DESCRIPTION')} ${lang}"`);
        }
        if ((this.initValues['productDetailedDescription' + lang] ?? '').trim() !== (this.form.value['productDetailedDescription' + lang] ?? '').trim()) {
          changes.push(`"${this.translate.instant('MENU.DETAILED_DESCRIPTION')} ${lang}"`);
        }
        if ((this.initValues['relatedProductsName' + lang] ?? '').trim() !== (this.form.value['relatedProductsName' + lang] ?? '').trim()) {
          changes.push(`"${this.translate.instant('MENU.DISHES.NEW.RELATED_PRODUCTS_NAME')} ${lang}"`);
        }
      }
    }

    if (this.initValues.defaultSizeId !== this.defaultSizeId){
      changes.push(`"${this.translate.instant('MENU.DISHES.NEW.DEFAULT_SIZE_ID')}"`);
    }
    return changes;
  }

  onEditModeChangedTags($event: boolean) {
    this.disableSubmit = $event;
  }

  onTagsChanged(newTags: string[]) {
    this.newTags = newTags;
    this.updateChangesInformationMessage();
  }

  onTagIdsChanged(newTagIds: string[]) {
    this.formTouched = true;
    this.newTagIds = newTagIds;
    this.updateChangesInformationMessage();
  }

  onRpProgramIdsChange(newRpProgramIds: string[]) {
    this.formTouched = true;
    this.rpProgramIds = newRpProgramIds;
    this.updateChangesInformationMessage();
  }

  async saveOverrides() {
    await this.saveProductOverrides();
    await this.saveProductStructureOverrides();
    await this.saveSizeStructureOverrides();
  }

  async saveProductOverrides() {
    let productOverride: ProductOverride;
    let isOverrideExists: boolean;
    let productOverridePosition = this.overridePositionsMap.getOverride(PositionTypeEnum.Product, this.data.product.externalId);
    // if override position does not exists create a new one
    if (!productOverridePosition) {
      productOverridePosition = {
        positionType: PositionTypeEnum.Product,
        positionId: this.data.product.externalId,
        override: emptyOverride,
        menuSectionId: this.data.menuSectionId,
      };
      productOverride = {};
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
      productOverride = JSON.parse(productOverridePosition.override) as ProductOverride;
    }

    const overrideInstructions: OverrideInstruction[] = []

    if(this.initValues['menuMediaUrl']?.trim() !== this.form.value['menuMediaUrl']?.trim()) {
      MenuOverrideService.overrideTextFiled(this.data.product, this.data.originalProduct, 'menuMediaUrl', this.form.value.menuMediaUrl, isOverrideExists, overrideInstructions, true);
    }
    if(this.initValues['cardMediaUrl']?.trim() !== this.form.value['cardMediaUrl']?.trim()) {
      MenuOverrideService.overrideTextFiled(this.data.product, this.data.originalProduct, 'cardMediaUrl', this.form.value.cardMediaUrl, isOverrideExists, overrideInstructions, true);
    }
    if (this.initValues['dishName'].trim() !== this.form.value['dishName'].trim()) {
      MenuOverrideService.overrideTextFiled(this.data.product, this.data.originalProduct, 'name', this.form.value.dishName, isOverrideExists, overrideInstructions);
    }

    if ((this.initValues['description'] ?? '').trim() !== (this.form.value['description'] ?? '').trim()) {
      MenuOverrideService.overrideTextFiled(this.data.product, this.data.originalProduct, 'description', this.form.value.description, isOverrideExists, overrideInstructions, true);
    }
    if ((this.initValues['detailedDescription'] ?? '').trim() !== (this.form.value['detailedDescription'] ?? '').trim()) {
      MenuOverrideService.overrideTextFiled(this.data.product, this.data.originalProduct, 'detailedDescription', this.form.value.detailedDescription, isOverrideExists, overrideInstructions, true);
    }
    if (this.initValues['externalPictureUrl'] !== this.form.value['externalPictureUrl']) {
      MenuOverrideService.overrideTextFiled(this.data.product, this.data.originalProduct, 'externalPictureUrl', this.form.value.picture, isOverrideExists, overrideInstructions);
    }

    if ([...this.initValues['tags']].sort().toString() !== [...this.newTags].sort().toString()) {
      MenuOverrideService.overrideArrayFiled(this.data.product, this.data.originalProduct, 'tags', this.newTags, isOverrideExists, overrideInstructions);
    }

    if ([...this.initValues['tagIds']].sort().toString() !== [...this.newTagIds].sort().toString()) {
      MenuOverrideService.overrideArrayFiled(this.data.product, this.data.originalProduct, 'tagIds', this.newTagIds, isOverrideExists, overrideInstructions);
    }

    if ([...this.initValues['rpProgramIds']].sort().toString() !== [...this.rpProgramIds].sort().toString()) {
      MenuOverrideService.overrideArrayFiled(this.data.product, this.data.originalProduct, 'relatedProductsProgramIds', this.rpProgramIds, isOverrideExists, overrideInstructions);
    }

    if (this.initValues['weight'] !== this.form.value['weight']) {
      MenuOverrideService.overrideNumberFiled(this.data.product, this.data.originalProduct, 'weight', this.form.value.weight, isOverrideExists, overrideInstructions);
    }

    if ((this.initValues['measure'] ?? '').trim() !== (this.form.value['measure'] ?? '').trim()) {
      MenuOverrideService.overrideTextFiled(this.data.product, this.data.originalProduct, 'measure', this.form.value.measure, isOverrideExists, overrideInstructions, true);
    }

    if (this.isNutritionFormChanged('nutrition')) {
      MenuOverrideService.overrideNutritionFiled(this.data.originalProduct.nutrition, this.getFormNutrition('nutrition'), isOverrideExists, overrideInstructions);
    }

    if (this.initValues['hideSizesImages'] !== this.hideSizesImages){
      MenuOverrideService.overrideBooleanFiled(this.data.product, this.data.originalProduct, 'hideSizesImages', this.hideSizesImages, isOverrideExists, overrideInstructions);
    }

    if (this.initValues['relatedProductsName']?.trim() !== this.form.value['relatedProductsName']?.trim()) {
      MenuOverrideService.overrideTextFiled(this.data.product, this.data.originalProduct, 'relatedProductsName', this.form.value.relatedProductsName, isOverrideExists, overrideInstructions, true);
    }

    if ([...this.initValues['relatedProducts']].sort().toString() !== [...this.relatedProducts].sort().toString()) {
      MenuOverrideService.overrideArrayFiled(this.data.product, this.data.originalProduct, 'relatedProducts', this.relatedProducts, isOverrideExists, overrideInstructions);
    }

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if (this.initValues['productName' + lang] !== this.form.value['productName' + lang]) {
          MenuOverrideService.overrideTranslationFiled(this.data.product, this.data.originalProduct, 'nameTranslations', lang, this.form.value['productName' + lang], isOverrideExists, productOverride, overrideInstructions);
        }
        if (this.initValues['productDescription' + lang] !== this.form.value['productDescription' + lang]) {
          MenuOverrideService.overrideTranslationFiled(this.data.product, this.data.originalProduct, 'descriptionTranslations', lang, this.form.value['productDescription' + lang], isOverrideExists, productOverride, overrideInstructions);
        }
        if ((this.initValues['productDetailedDescription' + lang] ?? '') !== (this.form.value['productDetailedDescription' + lang] ?? '')) {
          if (!this.data.product.detailedDescriptionTranslations) {
            this.data.product.detailedDescriptionTranslations = {};
          }
          MenuOverrideService.overrideTranslationFiled(this.data.product, this.data.originalProduct, 'detailedDescriptionTranslations', lang, this.form.value['productDetailedDescription' + lang], isOverrideExists, productOverride, overrideInstructions);
        }
        if (this.initValues['relatedProductsName' + lang] !== this.form.value['relatedProductsName' + lang]) {
          MenuOverrideService.overrideTranslationFiled(this.data.product, this.data.originalProduct, 'relatedProductsNameTranslations', lang, this.form.value['relatedProductsName' + lang], isOverrideExists, productOverride, overrideInstructions);
        }
      }
    }

    await this.saveOverridePosition(overrideInstructions, productOverridePosition);
  }

  async saveProductStructureOverrides() {
    let isOverrideExists: boolean;
    const positionType = this.data.product.productStructure ? PositionTypeEnum.ProductStructure : PositionTypeEnum.ComboStructure;
    let productStructureOverridePosition = this.overridePositionsMap.getOverride(positionType, this.data.product.externalId);
    // if override position does not exists create a new one
    if (!productStructureOverridePosition) {
      productStructureOverridePosition = {
        positionType: positionType,
        positionId: this.data.product.externalId,
        override: emptyOverride,
        menuSectionId: this.data.menuSectionId,
      };
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
    }

    const overrideInstructions: OverrideInstruction[] = []
    const productOrComboStructure = this.data.product.productStructure ? this.data.product.productStructure : this.data.product.comboStructure;
    const originalProductOrComboStructure = this.data.originalProduct.productStructure ? this.data.originalProduct.productStructure : this.data.originalProduct.comboStructure;
    if (this.initValues[ 'dishPrice' ] !== this.form.value[ 'dishPrice' ]) {
      MenuOverrideService.overrideNumberFiled(productOrComboStructure, originalProductOrComboStructure, 'price', this.form.value.dishPrice, isOverrideExists, overrideInstructions);
    }

    if (this.data.product.comboStructure && this.initValues['offerToCollect'] !== this.offerToCollect) {
      MenuOverrideService.overrideBooleanFiled(this.data.product.comboStructure, this.data.originalProduct.comboStructure, 'offerToCollect', this.offerToCollect, isOverrideExists, overrideInstructions);
    }

    if(this.data.product.productStructure && this.data.product.productStructure?.sizes.length > 0){
      if (this.initValues['sizeScaleId'] !== this.form.value.sizeScaleId){
        MenuOverrideService.overrideTextFiled(productOrComboStructure, originalProductOrComboStructure, 'sizeScaleId', this.form.value.sizeScaleId, isOverrideExists, overrideInstructions);
      }
    }
    if(this.data?.product.productStructure && this.data?.product.productStructure.modifierGroups.length > 0){
      if (this.initValues['modifiersDisplayType'] !== this.form.value.modifiersDisplayType){
        MenuOverrideService.overrideTextFiled(productOrComboStructure, originalProductOrComboStructure, 'modifiersDisplayType', this.form.value.modifiersDisplayType, isOverrideExists, overrideInstructions, true);
      }
    }

    if(this.initValues['allergenGroups'] && [...this.initValues['allergenGroups']]?.sort().toString() !== [...this.newAllergenGroups].sort().toString()) {
      MenuOverrideService.overrideArrayFiled(this.data.product.productStructure, this.data.originalProduct.productStructure, 'allergenGroups', this.newAllergenGroups, isOverrideExists, overrideInstructions);
    }

    if (this.data.product.productStructure && this.defaultSizeId !== this.initValues.defaultSizeId)
    {
      MenuOverrideService.overrideTextFiledEasy(
        this.data.originalProduct.productStructure,
        'defaultSizeId',
        this.defaultSizeId,
        isOverrideExists,
        overrideInstructions);
    }

    await this.saveOverridePosition(overrideInstructions, productStructureOverridePosition);
  }

  async saveSizeStructureOverrides() {
    const menuSectionId = this.data.menuSectionId;
    if (this.data.product.productStructure?.sizes.length > 0) {
      const sizeStructureOverridePositions: OverridePositionRequest[] = [];

      for (const sizeStructure of this.data.product.productStructure.sizes) {
        let sizeStructureOverride: SizeStructureOverride;
        let isOverrideExists: boolean;
        const positionId = `${this.data.product.externalId}_${sizeStructure.sizeId}`;
        let sizeStructureOverridePosition = this.overridePositionsMap.getOverride(PositionTypeEnum.SizeStructure, positionId);
        // if override position does not exists create a new one
        if (!sizeStructureOverridePosition) {
          sizeStructureOverridePosition = {
            positionType: PositionTypeEnum.SizeStructure,
            positionId: positionId,
            override: emptyOverride,
            menuSectionId: menuSectionId,
          };
          sizeStructureOverride = {};
        } else {
          // if override position exists update it anyway no matter has changes or not
          isOverrideExists = true;
          sizeStructureOverride = JSON.parse(sizeStructureOverridePosition.override) as SizeStructureOverride;
        }

        const overrideInstructions: OverrideInstruction[] = []
        const originalSize = this.data.originalProduct.productStructure.sizes.find(s => s.sizeId === sizeStructure.sizeId);
        if (this.initValues[sizeStructure.sizeId] !== this.form.value[sizeStructure.sizeId]) {
          MenuOverrideService.overrideNumberFiled(sizeStructure, originalSize, 'price', this.form.value[sizeStructure.sizeId], isOverrideExists, overrideInstructions);
        }
        if (this.initValues[sizeStructure.sizeId+"weight"] !== this.form.value[sizeStructure.sizeId+"weight"]) {
          MenuOverrideService.overrideNumberFiled(sizeStructure, originalSize, 'weight', this.form.value[sizeStructure.sizeId+"weight"], isOverrideExists, overrideInstructions);
        }
        if (this.initValues['isHidden' + sizeStructure.sizeId] !== this.form.value['isHidden' + sizeStructure.sizeId]) {
          MenuOverrideService.overrideBooleanFiled(sizeStructure, originalSize, 'isHidden', this.form.value['isHidden' + sizeStructure.sizeId], isOverrideExists, overrideInstructions);
        }
        if (this.initValues['externalPictureUrl'+sizeStructure.sizeId] !== this.form.value['externalPictureUrl'+sizeStructure.sizeId]) {
          MenuOverrideService.overrideTextFiled(sizeStructure, originalSize, 'externalPictureUrl', this.form.value['externalPictureUrl' + sizeStructure.sizeId], isOverrideExists, overrideInstructions, true);
        }
        if (this.isNutritionFormChanged('nutrition' + sizeStructure.sizeId)) {
          MenuOverrideService.overrideNutritionFiled(originalSize?.nutrition, this.getFormNutrition('nutrition' + sizeStructure.sizeId), isOverrideExists, overrideInstructions);
        }

        if (overrideInstructions.length > 0) {
          const newProductStructureOverridePosition: OverridePositionRequest = {
            ...sizeStructureOverridePosition,
            instructions: overrideInstructions,
          };
          newProductStructureOverridePosition.override = JSON.stringify(sizeStructureOverride);
          sizeStructureOverridePositions.push(newProductStructureOverridePosition);
        }
      }

      if (sizeStructureOverridePositions.length > 0) {
        const savedMenuSections = this.getSavedMenuSections();
        for (let sizeStructureOverridePosition of sizeStructureOverridePositions) {
          await this.menuService.saveOverridePositions(menuSectionId, [sizeStructureOverridePosition], savedMenuSections);
        }
      }
    }
  }

  private async saveOverridePosition(overrideInstructions: OverrideInstruction[], productOverridePosition: OverridePosition) {
    const menuSectionId = this.data.menuSectionId;
    if (overrideInstructions.length > 0) {
      const savedMenuSections = this.getSavedMenuSections();

      const productOverridePositionRequest: OverridePositionRequest = {
        ...productOverridePosition,
        instructions: overrideInstructions,
      };

      await this.menuService.saveOverridePositions(menuSectionId, [productOverridePositionRequest], savedMenuSections)
        .then(_=> _)
        .catch(error => alert(error.error));
    }
  }

  getSavedMenuSections(): string []  {
    return this.menuService.getSavedMenuSectionsIds(this.selectedMenuSections);
  }

  async submit() {
    if(!this.data.allowOverrides){
      return;
    }
    if (this.form.valid) {
      if (!this.data.product) {
        // this.saveNewProduct();
      } else {
        this.isBeingSaveProcessed = true;
        await this.saveOverrides();
        this.isBeingSaveProcessed = false;
      }
    }

    this.dialogRef.close();
  }

  toggleShowHideSizes($event: MatCheckboxChange) {
    if ($event.checked) {
      this.sizes = this.data.product.productStructure.sizes.sort((a,b) => a.order - b.order);
    } else {
      this.sizes = this.data.product.productStructure.sizes.filter(s => s.isHidden === false).sort((a,b) => a.order - b.order);
    }
  }

  toggleHideSizesImages($event: MatCheckboxChange) {
    this.hideSizesImages = $event.checked;
  }

  getSizeName(sizeId: string) {
    return this.dictionarySizes.get(sizeId).name;
  }

  sortModifierGroups(modifierGroupsStructure: ModifierGroupStructure[]): ModifierGroupStructure[] {
    return [...modifierGroupsStructure].sort(this.sortModifierGroupsByOrder)
  }

  sortComboGroups(modifierGroupsStructure: DisplayedComboGroupStructure[]): DisplayedComboGroupStructure[] {
    return [...modifierGroupsStructure].sort(this.sortComboGroupsByOrder)
  }

  sortModifierGroupsByOrder(p1: ModifierGroupStructure, p2: ModifierGroupStructure): number {
    if (p1.order > p2.order) {
      return 1;
    }
    if (p1.order < p2.order) {
      return -1;
    }
    return 0;
  }

  sortComboGroupsByOrder(p1: ComboGroupStructure, p2: ComboGroupStructure): number {
    if (p1.order > p2.order) {
      return 1;
    }
    if (p1.order < p2.order) {
      return -1;
    }
    return 0;
  }

  onImageChanged($event: string) {
    // Нужно иметь в виду, что на данный момент компонент, который вызывает этот метод,
    // удаляет картинку из бакета и создает новую, и если не сохранить эти изменения по картинкам на сервере в оверрайдах
    // (например досрочно закрыв окно продукта или нажав на отмену),
    // то это приведет к тому, что у продукта в оверрайдах останется удаленная ссылка.
    // Текущая реализация не позволяет эту ситуацию исправить в "несколько строчек кода" в каком-то одном проекте.
    this.form.get('picture').setValue($event);
    this.updateChangesInformationMessage();
  }

  onSizeImageChanged($event: string, sizeId: string) {
    this.form.get('externalPictureUrl' + sizeId).setValue($event);
    this.updateChangesInformationMessage();
  }

  isProductNameDifferent() {
    return this.data.originalProduct?.name.trim() !== this.form.value.dishName.trim();
  }

  isProductPriceDifferent() {
    if (this.data.originalProduct?.productStructure) {
      return this.data.originalProduct?.productStructure.price !== this.form.value.dishPrice;
    }

    return this.data.originalProduct?.comboStructure.price !== this.form.value.dishPrice;
  }

  isSizeScaleDifferent() : boolean {
    if(this.data.originalProduct?.productStructure){
      return this.data.originalProduct?.productStructure.sizeScaleId !== this.form.value['sizeScaleId'];
    }

    return false;
  }

  isProductSizePriceDifferent(sizeId) {
    return this.data.originalProduct?.productStructure?.sizes.find(s => s.sizeId === sizeId).price !== this.form.value[sizeId];
  }


  isProductSizeWeightDifferent(sizeId) {
    return this.data.originalProduct?.productStructure?.sizes.find(s => s.sizeId === sizeId).weight !== this.form.value[sizeId+"weight"];
  }

  isProductDescriptionDifferent() {
    return this.data.originalProduct?.description?.trim() !== this.form.value.description?.trim();
  }

  isProductDetailedDescriptionDifferent() {
    return this.data.originalProduct?.detailedDescription?.trim() !== this.form.value.detailedDescription?.trim();
  }

  isDefaultSizeDifferent(sourceSizeId: string, defaultSizeCheckbox: MatCheckbox): boolean {
    return this.originalDefaultSizesMap.get(sourceSizeId) !== defaultSizeCheckbox.checked
  }

  clearProductName() {
    this.form.patchValue({
      dishName: this.data.originalProduct.name,
    });
  }

  clearProductDescription() {
    this.form.patchValue({
      description: this.data.originalProduct.description ? this.data.originalProduct.description : '',
    });
  }

  clearProductDetailedDescription() {
    this.form.patchValue({
      detailedDescription: this.data.originalProduct.detailedDescription ? this.data.originalProduct.detailedDescription : '',
    });
  }

  clearProductPrice() {
    this.form.patchValue({
      dishPrice: this.data.originalProduct.productStructure ? this.data.originalProduct.productStructure.price : this.data.originalProduct.comboStructure.price,
    });
  }

  clearProductSizePrice(sizeId) {
    this.form.patchValue({
      [sizeId]: this.data.originalProduct?.productStructure.sizes.find(s => s.sizeId === sizeId).price,
    });
  }

  clearProductSizeWeight(sizeId) {
    this.form.patchValue({
      [sizeId+"weight"]: this.data.originalProduct?.productStructure.sizes.find(s => s.sizeId === sizeId).weight,
    });
  }

  clearDefaultSizeId() {
    this.defaultSizeId = this.data.originalProduct.productStructure.defaultSizeId;
    this.updateChangesInformationMessage();
  }

  getOriginalModifierGroupStructure(index: number): ModifierGroupStructure {
    if (this.data.originalProduct){
      return [...this.data.originalProduct.productStructure.modifierGroups].sort(this.sortModifierGroupsByOrder)[index];
    }
    else{
      return null;
    }
  }

  getOriginalSizePrice(sizeId: string) {
    return this.data.originalProduct.productStructure.sizes.find(s => s.sizeId === sizeId).price;
  }

  getOriginalSizeWeight(sizeId: string) {
    return this.data.originalProduct.productStructure.sizes.find(s => s.sizeId === sizeId).weight;
  }

  isProductWeightDifferent() {
    return this.data.originalProduct?.weight !== this.form.value.weight;
  }

  clearProductWeight() {
    this.form.patchValue({
      weight: this.data.originalProduct?.weight,
    });
  }

  isProductMeasureDifferent() {
    return (this.data.originalProduct?.measure ?? '').trim() !== (this.form.value.measure ?? '').trim();
  }

  clearProductMeasure() {
    this.form.patchValue({
      measure: this.data.originalProduct?.measure ? this.data.originalProduct.measure : '',
    });
  }

  getOriginalProductPrice() {
    if (this.data.originalProduct.productStructure) {
      return this.data.originalProduct?.productStructure.price;
    }

    return this.data.originalProduct?.comboStructure.price;
  }

  isProductNameTranslationDifferent(lang: string) {
    let originalValue = (this.data.originalProduct.nameTranslations[lang])?? '';
    const changedValue = this.form.value['productName' + lang].trim();
    return originalValue != changedValue;
  }

  clearProductNameTranslation(lang: string) {
    this.form.patchValue({
      ['productName' + lang]: this.data.originalProduct.nameTranslations[lang] ? this.data.originalProduct.nameTranslations[lang] : '',
    });
  }

  isProductDescriptionTranslationDifferent(lang: string) {
    let originalValue = (this.data.originalProduct.descriptionTranslations[lang])?? '';
    const changedValue = this.form.value['productDescription' + lang].trim();
    return originalValue != changedValue;
  }

  clearProductDescriptionTranslation(lang: string) {
    this.form.patchValue({
      ['productDescription' + lang]: this.data.originalProduct.descriptionTranslations[lang] ? this.data.originalProduct.descriptionTranslations[lang] : '',
    });
  }

  isProductDetailedDescriptionTranslationDifferent(lang: string) {
    let originalValue = (this.data.originalProduct.detailedDescriptionTranslations?.[lang])?? '';
    const changedValue = this.form.value['productDetailedDescription' + lang].trim();
    return originalValue != changedValue;
  }

  clearProductDetailedDescriptionTranslation(lang: string) {
    this.form.patchValue({
      ['productDetailedDescription' + lang]: this.data.originalProduct.detailedDescriptionTranslations?.[lang] ? this.data.originalProduct.detailedDescriptionTranslations[lang] : '',
    });
  }

  getOriginalComboGroupStructure(index: number): ComboGroupStructure {
    if (this.data.originalProduct){
      return [...this.data.originalProduct.comboStructure.groups].sort(this.sortComboGroupsByOrder)[index];
    }
    else {
      return null;
    }
  }

  getOriginalDescriptionTranslation(lang: string) {
    if (this.data.originalProduct && this.data.originalProduct.descriptionTranslations && this.data.originalProduct.descriptionTranslations.hasOwnProperty(lang)) {
      return this.data.originalProduct.descriptionTranslations[lang];
    }

    return '';
  }

  getOriginalNameTranslation(lang: string) {
    if (this.data.originalProduct && this.data.originalProduct.nameTranslations && this.data.originalProduct.nameTranslations.hasOwnProperty(lang)) {
      return this.data.originalProduct.nameTranslations[lang];
    }

    return 'undefined';
  }

  onMenuSelectedChanges($event: object) {
    this.selectedMenuSections = $event;
  }

  trackByFn(index, item: ModifierGroupStructure) {
    return item.modifierGroupId;
  }

  editSize(sizeId: string) {
    const size = this.dictionarySizes.get(sizeId);
    const originalSize = this.originalDictionarySizes.get(sizeId);
    const editSizeModalComponentData: EditSizeModalComponentDataType = {
      size,
      originalSize,
      organization: this.data.organization,
      menuSectionId: this.data.menuSectionId,
      menuSections: this.data.menuSections,
      overridePositionsMap: this.overridePositionsMap,
    };
    this.dialog.open(EditSizeModalComponent, {
      data: editSizeModalComponentData,
    });
  }

  isChangedSize(sizeId: string): boolean{
    return this.dictionarySizes.get(sizeId).isChangedSize;
  }

  getOriginalSize(sizeId: string) : SizeStructure {
    return this.data.originalProduct.productStructure.sizes.find(s => s.sizeId == sizeId);
  }

  changeSaveForAllCombo(value){
    this.saveForAllCombo = value;
  }

  changeSaveForAll(value){
    this.saveForAll = value;
  }

  openEditSizeScaleModal(sizeScaleId: string | null = null) {
    // sizeScaleId is null means create new SizeScale.
    let sizeScale: SizeScale;
    if(sizeScaleId){
      sizeScale = this.sizeScales.find(ss => ss.id == sizeScaleId);
    } else {
      sizeScale = {
        name: "",
        nameTranslations: {},
        visualType: "",
      }
    }

    this.dialog.open(EditSizeScaleModalComponent, {
      data: {
        sizeScaleId: sizeScaleId,
        sizeScale: sizeScale,
        organization: this.data.organization,
        menuSectionId: this.data.menuSectionId
      } as EditSizeScaleModalComponentDataType
    },).afterClosed().subscribe(result => {
      const updatedSizeScale = result[0] as DisplayedSizeScale;
      if (result == DELETE_SIZE_SCALE){
        this.deleteSizeScale(this.form.value['sizeScaleId']);
      } else if(!sizeScaleId){
        if(updatedSizeScale && updatedSizeScale.id){
          this.form.value['sizeScaleId'] = updatedSizeScale.id;
        }
      }
    });
  }

  openDeleteSizeScaleDialog(){
    const sizeScaleId = this.form.value['sizeScaleId'];
    this.sizeScaleService.canDeleteSizeScale(this.data.menuSectionId, sizeScaleId).subscribe(result => {
      this.dialog.open(DeleteSizeScaleDialogComponent, {
        data: {
          usingScalesInProducts: result
        } as DeleteSizeScaleDialogData
      }).afterClosed().subscribe(result => {
        if(result == DELETE_SIZE_SCALE) {
          this.deleteSizeScale(sizeScaleId);
        }
      })
    });
  }

  deleteSizeScale(sizeScaleId: string) {
    let confirmation = confirm('Are you sure delete?')
    if (!confirmation) {
      return;
    }
    this.sizeScaleService.deleteSizeScale(this.data.organization.id, sizeScaleId).subscribe()

    const updatedSizeScale = this.sizeScales.filter(ss => ss.id !== sizeScaleId);
    this.store.dispatch(setSizeScales(updatedSizeScale));

    this.form.value['sizeScaleId'] = null;
  }

  onAllergenGroupIdsChange($event: string[]) {
    this.newAllergenGroups = $event;
    this.formTouched = true;
    this.updateChangesInformationMessage();
  }

  changeSaveForAllProductInComboGroup(value: boolean) {
    this.saveForAllProductsInComboGroup = value;
  }

  onVideoChanged($event: string, fieldName: string) {
    this.form.get(fieldName).setValue($event);
    this.updateChangesInformationMessage();
  }


  editRelatedProducts() {
    const productIds = this.relatedProducts;

    this.dialog.open(SelectProductsModalComponent, {
      data: {
        title: this.translate.instant('MENU.DISHES.NEW.SELECT_RELATED_PRODUCTS', {productName: this.data.product.name}),
        productIds: productIds,
      } as SelectProductsModalComponentDataType
    }).afterClosed().subscribe(async res => {
      if (res) {
        const newProductIds = res.data;
        this.relatedProducts = newProductIds;
        this.formTouched = true;
      }
    });
    this.updateChangesInformationMessage();
  }

  isRelatedProductsDifferent() : boolean {
    return [...this.relatedProducts].sort().toString() !== [...this.data.originalProduct.relatedProducts].sort().toString();
  }

  restoreRelatedProducts(){
    this.relatedProducts = this.data.originalProduct.relatedProducts;
    this.formTouched = true;
    this.updateChangesInformationMessage();
  }

  isRelatedProductsNameDifferent() {
    return this.form.value['relatedProductsName'];
  }

  clearRelatedProductsName() {
    this.form.patchValue({'relatedProductsName': null});
  }

  isRelatedProductsNameTranslationsDifferent(lang: string) {
    return this.form.value['relatedProductsName' + lang];
  }

  clearRelatedProductsNameTranslations(lang: string) {
    this.form.patchValue({ ['relatedProductsName' + lang]: null});
  }

  changeDefaultSizeId(sourceSizeId: string , $event: MatCheckboxChange) {
    if (!$event.checked){// отключение дефолтного размера.
      if (this.data.originalProduct.productStructure.defaultSizeId){
        this.defaultSizeId = defaultSizeDisabled;
      }
      else{
        this.defaultSizeId = null;
      }
    }
    else{
      this.defaultSizeId = sourceSizeId;
    }
    this.updateChangesInformationMessage();
  }

  convertSizesToMap(originalSizes: SizeStructure[]): Map<string, boolean>
  {
    if (!originalSizes || originalSizes.length === 0) {
      return new Map<string, boolean>();
    }

    return new Map(originalSizes.map(s => [
      s.sizeId,
      s.sizeId === this.data.originalProduct.productStructure?.defaultSizeId]))
  }
}

export interface EditProductsModalComponentDataType {
  product: DisplayedProduct;
  originalProduct: Product;
  menuSections: MenuSection[];
  menuSectionId: string;
  organization: Organization;
  organizationId: string;
  comboGroupStructure?: DisplayedComboGroupStructure;
  saveForAllProductsInComboGroup?: boolean;
  allowOverrides: boolean;
}

